// LivePreviewPanel — visual, live-updating snapshot of what's in the quote.
// Rendered inline in the QuoteBuilderV2 sidebar on desktop, and inside a
// bottom-sheet drawer on mobile via <MobilePreviewFab />.
//
// Every field in the quote object (customer, sections, GP%) flows straight
// through — the parent passes `quote` + `totals` and this stays in sync
// automatically because it's a pure render component.
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Download, FileText, Loader2, Eye, Ruler, DoorOpen, Package, Hammer, HardHat, Sparkles, FileScan, MousePointerClick, Share2, MessageCircle, Phone, Mail, ClipboardCopy, Palette, CalendarClock } from "lucide-react";
import { fmtCurrencyExact, fmtCurrency, FENCE_LABELS } from "@/lib/constants";
import { useState, useMemo, useEffect } from "react";
import { COLORBOND_COLOURS } from "@/lib/fencing";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SECTION_ICON = {
  fence: Ruler,
  gate: DoorOpen,
  extra: Package,
  labour: HardHat,
  fabrication: Hammer,
  daily: CalendarClock,
};

const SECTION_LABEL = {
  fence: "Fence",
  gate: "Gate",
  extra: "Extra",
  labour: "Labour",
  fabrication: "Fabrication",
  daily: "Daily",
};

function SectionRow({ section, cost, onClick }) {
  const Icon = SECTION_ICON[section.section_type] || Sparkles;
  const label = SECTION_LABEL[section.section_type] || "Section";
  const swatch = section.colour_hex || null;
  const dims = [];
  if (section.length_m) dims.push(`${section.length_m}m`);
  if (section.height_mm) dims.push(`${(section.height_mm / 1000).toFixed(1)}m H`);
  if (section.section_type === "daily" && section.units && section.qty) {
    dims.push(`${section.units} × ${section.qty} day${section.qty === 1 ? "" : "s"}`);
  } else if (section.qty && (section.qty > 1 || section.section_type !== "fence")) {
    dims.push(`×${section.qty}`);
  }
  const gate = section.gate_template_id
    ? section.gate_template_id.replaceAll("_", " ")
    : null;
  const fromPlan = section.source === "site_plan";

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`preview-section-${section.id}`}
      className="w-full text-left flex items-start gap-2.5 py-2.5 border-b border-slate-100 last:border-b-0 hover:bg-cyan-50/60 focus:bg-cyan-50 focus:outline-none focus:ring-1 focus:ring-cyan-400 rounded-sm px-1 -mx-1 transition-colors group"
      aria-label={`Jump to ${label} section: ${section.label || "untitled"}`}
    >
      <div className="flex-shrink-0 w-8 h-8 rounded-sm bg-cyan-50 border border-cyan-100 flex items-center justify-center mt-0.5 group-hover:bg-cyan-100 group-hover:border-cyan-300 transition-colors">
        <Icon size={14} className="text-cyan-700" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 font-mono">{label}</span>
          <span className="text-sm font-semibold text-slate-800 truncate">{section.label || "Untitled"}</span>
          {fromPlan && (
            <Badge
              variant="outline"
              className="rounded-sm h-5 px-1.5 text-[10px] font-mono uppercase tracking-wider border-violet-300 bg-violet-50 text-violet-700 flex items-center gap-1"
              title="Imported from the AI Site Plan Reader"
              data-testid={`preview-section-from-plan-${section.id}`}
            >
              <FileScan size={9} /> from PDF
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap mt-1 text-[11px] text-slate-500 font-mono">
          {section.fence_type && (
            <Badge variant="outline" className="rounded-sm h-5 px-1.5 text-[10px] uppercase tracking-wider border-slate-200 font-mono" data-testid={`preview-fence-type-${section.id}`}>
              {FENCE_LABELS[section.fence_type] || section.fence_type.replaceAll("_", " ")}
            </Badge>
          )}
          {dims.length > 0 && <span>{dims.join(" · ")}</span>}
          {gate && (
            <Badge variant="outline" className="rounded-sm h-5 px-1.5 text-[10px] tracking-wider border-emerald-200 text-emerald-700 font-mono">
              {gate}
            </Badge>
          )}
          {swatch && (
            <div className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-sm border border-slate-300" style={{ background: swatch }} />
              <span className="text-slate-500 truncate max-w-[110px]">{section.colour || "Colour"}</span>
            </div>
          )}
          {section.gate_style && (
            <span className="text-slate-400">· {section.gate_style}</span>
          )}
          {section.man_days && (
            <span className="text-amber-700">· {section.man_days} man-day{section.man_days === 1 ? "" : "s"}</span>
          )}
        </div>
        {section.notes && (
          <div className="text-[11px] text-slate-400 mt-1 italic truncate">“{section.notes}”</div>
        )}
      </div>
      <div className="text-right shrink-0 pt-1 flex flex-col items-end gap-0.5">
        <span className="font-mono text-slate-700 font-semibold text-xs">
          {fmtCurrencyExact(cost)}
        </span>
        <MousePointerClick size={10} className="text-slate-300 group-hover:text-cyan-600 transition-colors" />
      </div>
    </button>
  );
}

/**
 * Live preview panel body — pure UI. Consumes the same `quote`, `totals`,
 * and `sectionCost` the QuoteBuilderV2 sidebar uses so it stays in perfect
 * sync as the user edits. `onSectionClick(sectionId)` fires when the user
 * taps a section row — the parent scrolls to that section in the form.
 */
export function LivePreviewBody({ quote, totals, sectionCost, onDownload, onOpenFull, downloading, opening, quoteId, onSectionClick, shareResetKey }) {
  const sections = quote?.sections || [];
  const planCount = sections.filter((s) => s.source === "site_plan").length;

  // Unique Colorbond colour palette used across the quote (up to 6 chips).
  // Deps use quote?.sections directly to avoid re-running the memo every
  // render when quote is undefined (a fresh `[] || sections` ref each time).
  const palette = useMemo(() => {
    const seen = new Set();
    const chips = [];
    for (const s of sections) {
      const key = (s.colour || s.colour_hex || "").toString().trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      // Look up hex from the shared Colorbond catalogue as a fallback.
      const cat = COLORBOND_COLOURS.find((c) => c.name.toLowerCase() === (s.colour || "").toLowerCase());
      chips.push({
        name: s.colour || (s.colour_hex ? "Custom" : "Colour"),
        hex: s.colour_hex || cat?.hex || "#94a3b8",
      });
      if (chips.length >= 6) break;
    }
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.sections]);

  return (
    <div className="space-y-3" data-testid="live-preview-body">
      {/* Customer + address header */}
      <div className="border border-slate-200 rounded-sm bg-slate-50 p-3">
        <div className="stamp-label text-cyan-600 mb-1">Customer</div>
        <div className="font-semibold text-slate-800 text-sm truncate" data-testid="preview-customer-name">
          {quote?.customer_name || <span className="text-slate-400 italic">Untitled quote</span>}
        </div>
        {quote?.site_address && (
          <div className="text-[11px] text-slate-500 mt-0.5 truncate font-mono">{quote.site_address}</div>
        )}
        {quote?.customer_email && (
          <div className="text-[11px] text-slate-500 mt-0.5 truncate font-mono">{quote.customer_email}</div>
        )}
        {quote?.customer_phone && (
          <div className="text-[11px] text-slate-500 mt-0.5 truncate font-mono">{quote.customer_phone}</div>
        )}
      </div>

      {/* Colour palette strip — hero preview of what the fence will look like */}
      {palette.length > 0 && (
        <div className="border border-slate-200 rounded-sm bg-white overflow-hidden" data-testid="preview-colour-strip">
          <div className="stamp-label text-slate-500 px-3 pt-3 mb-2 flex items-center gap-1.5">
            <Palette size={11} className="text-cyan-600" />
            {palette.length === 1 ? "Fence colour" : `Fence colours (${palette.length})`}
          </div>
          {/* Hero strip — first colour gets a large 88px band, remaining show as chips below */}
          <div
            className="h-24 w-full relative"
            style={{ background: palette[0].hex }}
            data-testid="preview-colour-hero"
            title={palette[0].name}
          >
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/40 to-transparent px-3 py-2">
              <div className="text-white font-display font-semibold text-base tracking-wide drop-shadow" data-testid="preview-colour-hero-name">
                {palette[0].name}
              </div>
              <div className="text-white/80 text-[10px] font-mono">{palette[0].hex.toUpperCase()}</div>
            </div>
          </div>
          {palette.length > 1 && (
            <div className="flex gap-1.5 px-3 py-2 flex-wrap" data-testid="preview-colour-chips">
              {palette.slice(1).map((c) => (
                <div key={c.hex + c.name} className="flex items-center gap-1.5 rounded-sm border border-slate-200 pl-1 pr-2 py-0.5 bg-white" title={c.hex}>
                  <span className="w-4 h-4 rounded-sm border border-slate-300" style={{ background: c.hex }} />
                  <span className="text-[11px] font-mono text-slate-600">{c.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sale price highlight */}
      <div className="border border-slate-200 rounded-sm bg-slate-900 text-white p-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="stamp-label text-cyan-400">Sale price</div>
            <div className="font-display text-2xl font-bold tracking-tight" data-testid="preview-sale-price">
              {fmtCurrency(totals?.sale || 0)}
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5 font-mono">
              AUD ex GST · {sections.length} section{sections.length === 1 ? "" : "s"} · GP {(totals?.gp || 0).toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      {/* Section list */}
      <div className="border border-slate-200 rounded-sm bg-white p-3">
        <div className="flex items-center justify-between mb-1">
          <div className="stamp-label text-slate-500">Selected sections</div>
          {planCount > 0 && (
            <Badge
              variant="outline"
              className="rounded-sm h-5 px-1.5 text-[10px] font-mono uppercase tracking-wider border-violet-300 bg-violet-50 text-violet-700 flex items-center gap-1"
              title={`${planCount} of ${sections.length} sections came from the AI Site Plan Reader`}
              data-testid="preview-plan-count"
            >
              <FileScan size={9} /> {planCount} from PDF
            </Badge>
          )}
        </div>
        {sections.length === 0 ? (
          <div className="text-xs text-slate-400 py-3 text-center italic" data-testid="preview-empty">
            No sections added yet. Add a fence, gate, extra or labour section — this preview updates automatically.
          </div>
        ) : (
          <>
            <div className="divide-y divide-slate-100" data-testid="preview-section-list">
              {sections.map((s) => (
                <SectionRow
                  key={s.id}
                  section={s}
                  cost={sectionCost(s)}
                  onClick={onSectionClick ? () => onSectionClick(s.id) : undefined}
                />
              ))}
            </div>
            {onSectionClick && (
              <div className="text-[10px] text-slate-400 italic text-center pt-2 font-mono">
                Tap a section to jump to it in the form
              </div>
            )}
          </>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2">
        {onDownload ? (
          <Button
            data-testid="preview-download-pdf-btn"
            onClick={onDownload}
            disabled={downloading || sections.length === 0}
            className="rounded-sm h-11 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold w-full"
          >
            {downloading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Download size={16} className="mr-2" />}
            Download preview PDF
          </Button>
        ) : (
          <div className="rounded-sm border border-amber-200 bg-amber-50 text-amber-800 text-xs p-2.5 text-center" data-testid="pdf-locked-note">
            PDF download unlocks once the quote is <b>approved</b>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Button
            data-testid="preview-open-full-btn"
            onClick={onOpenFull}
            disabled={opening || sections.length === 0}
            variant="outline"
            className="rounded-sm h-10 border-slate-300"
          >
            {opening ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Eye size={14} className="mr-1.5" />}
            Full PDF
          </Button>
          <ShareToClientButton quote={quote} quoteId={quoteId} sectionsCount={sections.length} resetKey={shareResetKey} />
        </div>
        {!quoteId && (
          <p className="text-[10px] text-slate-400 italic text-center mt-1">
            Download will save the quote as a draft first (adds a quote number).
          </p>
        )}
      </div>

      {/* Share Audit Log */}
      {quoteId && <ShareAuditLog quoteId={quoteId} resetKey={shareResetKey} />}
    </div>
  );
}

// -------- Preview Share (WhatsApp / SMS / Copy) --------

const digitsOnly = (s) => (s || "").replace(/[^\d+]/g, "");
const toIntlAU = (s) => {
  const d = digitsOnly(s);
  if (!d) return "";
  if (d.startsWith("+")) return d.slice(1);        // wa.me wants no leading +
  if (d.startsWith("0")) return "61" + d.slice(1); // AU local → +61
  return d;
};

function ShareToClientButton({ quote, quoteId, sectionsCount, resetKey }) {
  const [sharing, setSharing] = useState(false);
  const [open, setOpen] = useState(false);
  const [shareState, setShareState] = useState(null); // {url, expires_at}
  const phone = quote?.customer_phone || "";
  const disabled = !quoteId || sectionsCount === 0;

  // Reset the cached share link whenever the parent bumps the reset signal
  // (e.g. after Revoke Links). Without this, tapping Share again after
  // revoke would reuse a token that's now dead on the server.
  useEffect(() => {
    setShareState(null);
  }, [resetKey, quoteId]);

  const ensureLink = async () => {
    if (shareState?.url) return shareState;
    setSharing(true);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/share`);
      const url = `${window.location.origin}/portal/quote/${data.quote_id}?t=${data.token}`;
      const s = { url, expires_at: data.expires_at };
      setShareState(s);
      return s;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate share link");
      throw e;
    } finally {
      setSharing(false);
    }
  };

  const buildMessage = (url) => {
    const name = quote?.customer_name?.split(" ")[0] || "there";
    const nr = quote?.quote_number ? ` (${quote.quote_number})` : "";
    return `Hi ${name}, your CCFI quote${nr} is ready to view here: ${url} — reply if you have any questions.`;
  };

  const shareVia = async (kind) => {
    if (disabled) {
      toast.error(sectionsCount === 0 ? "Add at least one section first." : "Save the quote first.");
      return;
    }
    try {
      const s = await ensureLink();
      const msg = buildMessage(s.url);
      if (kind === "whatsapp") {
        const target = toIntlAU(phone);
        const href = target
          ? `https://wa.me/${target}?text=${encodeURIComponent(msg)}`
          : `https://wa.me/?text=${encodeURIComponent(msg)}`;
        window.open(href, "_blank", "noopener");
      } else if (kind === "sms") {
        // sms: URI — some Android phones prefer body= after ?, iOS uses &body= after phone
        const href = phone ? `sms:${phone}?&body=${encodeURIComponent(msg)}` : `sms:?&body=${encodeURIComponent(msg)}`;
        window.location.href = href;
      } else if (kind === "email") {
        const to = quote?.customer_email || "";
        const subj = `Your CCFI quote${quote?.quote_number ? ` — ${quote.quote_number}` : ""}`;
        window.location.href = `mailto:${to}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(msg)}`;
      } else if (kind === "copy") {
        try {
          await navigator.clipboard.writeText(s.url);
          toast.success("Client link copied to clipboard.");
        } catch {
          toast.success("Client link: " + s.url);
        }
      } else if (kind === "native" && navigator.share) {
        await navigator.share({ title: "CCFI quote", text: msg, url: s.url });
      }
      setOpen(false);
    } catch { /* ensureLink already toasted */ }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid="preview-share-client-btn"
          disabled={disabled || sharing}
          variant="outline"
          className="rounded-sm h-10 border-emerald-500 text-emerald-700 hover:bg-emerald-50"
        >
          {sharing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Share2 size={14} className="mr-1.5" />}
          Share
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2 rounded-sm" align="end" data-testid="preview-share-popover">
        <div className="stamp-label text-slate-500 px-2 pt-1 pb-2">Send this quote to</div>
        <button
          type="button"
          data-testid="share-whatsapp-btn"
          onClick={() => shareVia("whatsapp")}
          className="w-full flex items-center gap-2 px-2 py-2 rounded-sm hover:bg-emerald-50 text-sm text-slate-700 text-left"
        >
          <MessageCircle size={16} className="text-emerald-600" />
          <div className="flex-1">
            <div className="font-medium">WhatsApp</div>
            {phone && <div className="text-[10px] text-slate-400 font-mono truncate">{phone}</div>}
          </div>
        </button>
        <button
          type="button"
          data-testid="share-sms-btn"
          onClick={() => shareVia("sms")}
          className="w-full flex items-center gap-2 px-2 py-2 rounded-sm hover:bg-cyan-50 text-sm text-slate-700 text-left"
        >
          <Phone size={16} className="text-cyan-600" />
          <div className="flex-1">
            <div className="font-medium">SMS / iMessage</div>
            {phone && <div className="text-[10px] text-slate-400 font-mono truncate">{phone}</div>}
          </div>
        </button>
        <button
          type="button"
          data-testid="share-email-btn"
          onClick={() => shareVia("email")}
          className="w-full flex items-center gap-2 px-2 py-2 rounded-sm hover:bg-slate-100 text-sm text-slate-700 text-left"
        >
          <Mail size={16} className="text-slate-500" />
          <div className="flex-1">
            <div className="font-medium">Email</div>
            {quote?.customer_email && <div className="text-[10px] text-slate-400 font-mono truncate">{quote.customer_email}</div>}
          </div>
        </button>
        <button
          type="button"
          data-testid="share-copy-btn"
          onClick={() => shareVia("copy")}
          className="w-full flex items-center gap-2 px-2 py-2 rounded-sm hover:bg-slate-100 text-sm text-slate-700 text-left"
        >
          <ClipboardCopy size={16} className="text-slate-500" />
          <div className="font-medium">Copy link</div>
        </button>
        {!phone && (
          <div className="mt-2 px-2 py-1.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm">
            No customer phone on file — WhatsApp / SMS will open with a blank recipient.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// -------- Share Audit Log (list of every create + revoke event) --------

function shortAgo(iso) {
  if (!iso) return "";
  const ts = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function ShareAuditLog({ quoteId, resetKey }) {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/quotes/${quoteId}/share/events`);
      setEvents(data.events || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load share history");
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && events === null) load();
  };

  // When the parent bumps resetKey (e.g. after Revoke Links or a fresh
  // mint), refresh the events list if the audit is expanded so the user
  // sees the change immediately.
  useEffect(() => {
    if (expanded) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, quoteId]);

  const activeCount = (events || []).filter((e) => e.event === "created" && e.is_active).length;

  return (
    <div className="border border-slate-200 rounded-sm bg-white overflow-hidden" data-testid="share-audit-log">
      <button
        type="button"
        data-testid="share-audit-toggle"
        onClick={toggle}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FileText size={12} className="text-slate-500" />
          <span className="stamp-label text-slate-500">Share history</span>
          {events !== null && (
            <span className="text-[10px] font-mono text-slate-400" data-testid="share-audit-summary">
              · {events.length} event{events.length === 1 ? "" : "s"}
              {activeCount > 0 && (
                <span className="ml-1 text-emerald-600 font-semibold">· {activeCount} active</span>
              )}
            </span>
          )}
        </div>
        <span className="text-xs text-slate-400 font-mono">{expanded ? "hide" : "show"}</span>
      </button>
      {expanded && (
        <div className="border-t border-slate-100 max-h-64 overflow-y-auto" data-testid="share-audit-body">
          {loading && <div className="p-3 text-xs text-slate-400 italic text-center"><Loader2 size={12} className="inline animate-spin mr-1" /> Loading…</div>}
          {events && events.length === 0 && !loading && (
            <div className="p-3 text-xs text-slate-400 italic text-center" data-testid="share-audit-empty">
              No share activity yet.
            </div>
          )}
          {events && events.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {events.map((ev) => (
                <li key={ev.id} className="px-3 py-2 flex items-start gap-2 text-[11px] font-mono" data-testid={`share-audit-event-${ev.event}`}>
                  <div className="mt-0.5 shrink-0">
                    {ev.event === "created" ? (
                      ev.is_active
                        ? <span className="w-2 h-2 rounded-full bg-emerald-500 block ring-2 ring-emerald-100" title="Currently active" />
                        : <span className="w-2 h-2 rounded-full bg-slate-300 block" title="Expired or superseded" />
                    ) : (
                      <span className="w-2 h-2 rounded-sm bg-rose-500 block" title="Revoked" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`font-semibold ${ev.event === "revoked" ? "text-rose-700" : ev.is_active ? "text-emerald-700" : "text-slate-500"}`}>
                        {ev.event === "created" ? "Link issued" : `Revoked ${ev.revoked_count || 0} link${ev.revoked_count === 1 ? "" : "s"}`}
                      </span>
                      {ev.event === "created" && (
                        <span className="text-slate-400">· suffix {ev.token_suffix || "—"}</span>
                      )}
                    </div>
                    <div className="text-slate-500 mt-0.5">
                      by <span className="text-slate-700">{ev.by_name || ev.by_email || "system"}</span>
                      {" · "}
                      <span title={ev.at}>{shortAgo(ev.at)}</span>
                    </div>
                    {ev.event === "created" && ev.expires_at && (
                      <div className="text-slate-400 text-[10px] mt-0.5">
                        Expires {new Date(ev.expires_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-slate-100 p-2">
            <Button size="sm" variant="ghost" onClick={load} disabled={loading} className="rounded-sm h-7 text-[10px] w-full font-mono" data-testid="share-audit-refresh">
              {loading ? <Loader2 size={10} className="animate-spin mr-1" /> : null}
              Refresh
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Desktop-side sticky panel: renders directly in the sidebar column.
 */
export function LivePreviewPanel(props) {
  return (
    <Card className="rounded-sm border-slate-200 industrial-shadow p-4 space-y-3 lg:block" data-testid="live-preview-panel">
      <div className="flex items-center gap-2">
        <FileText size={14} className="text-cyan-600" />
        <div className="stamp-label text-cyan-600">Live preview</div>
      </div>
      <LivePreviewBody {...props} />
    </Card>
  );
}

/**
 * Mobile FAB — floats bottom-right on small screens; tapping opens a
 * bottom sheet with the same live preview + download button. Tapping a
 * section auto-closes the sheet then the parent handles the scroll.
 */
export function MobilePreviewFab({ quote, onSectionClick, ...props }) {
  const [open, setOpen] = useState(false);
  const count = quote?.sections?.length || 0;
  const handleSectionTap = (sid) => {
    setOpen(false);
    // small delay so the sheet close animation runs before the smooth scroll
    setTimeout(() => onSectionClick?.(sid), 240);
  };
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid="mobile-preview-fab"
          className="lg:hidden fixed bottom-5 right-5 z-40 h-14 pl-4 pr-5 rounded-full shadow-2xl bg-cyan-600 hover:bg-cyan-700 text-white flex items-center gap-2 font-semibold ring-4 ring-white/40 transition active:scale-95"
        >
          <Eye size={18} />
          <span>Preview</span>
          <span className="inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-full bg-white/25 text-[11px] font-mono">
            {count}
          </span>
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="rounded-t-2xl h-[92vh] overflow-y-auto pt-4 px-4 pb-8" data-testid="mobile-preview-sheet">
        <SheetHeader className="pb-2">
          <SheetTitle className="font-display text-xl flex items-center gap-2">
            <FileText size={18} className="text-cyan-600" /> Live preview
          </SheetTitle>
        </SheetHeader>
        <LivePreviewBody quote={quote} onSectionClick={handleSectionTap} {...props} />
      </SheetContent>
    </Sheet>
  );
}
